/**
 * <ExtraHoursRecapStep> — l'achat que le parent valide avant que sa demande parte (organism).
 *
 * La dernière étape n'est pas un récapitulatif de formulaire, c'est la validation d'un achat : des
 * parents prenaient ce geste pour un changement de planning et découvraient après coup qu'ils
 * avaient acheté une heure en plus. Elle dit donc, dans cet ordre : ce qu'on achète et pour qui
 * (en plus de l'abonnement, sans rien changer aux séances habituelles), le détail et le prix TTC,
 * quand l'argent part, puis les créneaux cochés ou les dates demandées. Rien à facturer
 * (`recapKind="request"`), le parent ne l'achète pas : il le demande, et la phrase le dit.
 *
 * Aucun prix n'est calculé ici : `totalLabel` arrive déjà écrit — le tarif ferme du backend × les
 * heures FACTURÉES —, et la phrase de facturation se contente de l'entourer. Quand l'argent part
 * (`billing`) est une règle de l'adaptateur (`extraHoursBilling`), qui écrit aussi ce que l'heure du
 * mois de l'offre Autonomie fait à la demande (`autonomyNote`) : ce composant ne compte aucune heure.
 *
 * Ce qui bloque l'envoi ne s'affiche PAS ici mais au pied de la feuille, contre le bouton :
 * une raison de refus posée au fond d'un corps qui défile ne se lit jamais, et le parent reste
 * devant un bouton éteint sans savoir pourquoi.
 */
import React from "react"

import { Divider } from "../../atoms/Divider"
import { Stack } from "../../atoms/containers/Stack"
import { Paragraph } from "../../atoms/typography/Paragraph"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { Card } from "../../molecules/Card"
import { SummaryRow } from "../../molecules/SummaryRow"
import { formatDayMonthYear, formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import {
    type UIExtraHoursBilling,
    type UIExtraHoursDeadlineOption,
    type UIExtraHoursDraft,
    type UIExtraHoursFormatOption,
    type UIExtraHoursRecapKind,
    type UISubject,
    type UITimeSlotDay,
} from "../../../ports/primary/ui/types"
import { SlotChips } from "./SlotChips"

export type ExtraHoursRecapStepProps = {
    draft: UIExtraHoursDraft
    /** Prénom de l'enfant pour qui l'heure est achetée. */
    childFirstname: string
    /** Catalogues, pour renommer ce que le brouillon ne porte qu'en identifiant. */
    formatOptions: UIExtraHoursFormatOption[]
    deadlineOptions: UIExtraHoursDeadlineOption[]
    subjects: UISubject[]
    days: UITimeSlotDay[]
    /** Le prix TTC de ce qui se facture, déjà écrit — « 28 € ». */
    totalLabel: string
    /** Quand la demande se facture. `none` : rien à facturer. */
    billing: UIExtraHoursBilling
    /** Ce que l'heure du mois de l'offre Autonomie fait à la demande, déjà écrit — absent hors Autonomie. */
    autonomyNote?: string
    /** Un achat, ou une demande quand rien ne se facture. Défaut `purchase`. */
    recapKind?: UIExtraHoursRecapKind
    testID?: string
}

/** Quand l'argent part, sous le prix. Rien à facturer : pas de phrase, la ligne Autonomie le dit. */
function billingMessage(billing: UIExtraHoursBilling, totalLabel: string): string | null {
    switch (billing) {
        case "immediate":
            return `Une fois le cours planifié par notre équipe, ces ${totalLabel} sont prélevés immédiatement, en une fois : votre abonnement n'a pas de prochain prélèvement.`
        case "none":
            return null
        default:
            return `Une fois le cours planifié par notre équipe, ces ${totalLabel} vous sont facturés en une fois sur votre prochain prélèvement, en plus de l'abonnement.`
    }
}

/**
 * Le brouillon garde la date au format de la demande (`YYYY-MM-DD HH:mm`) ; les formatters
 * lisent de l'ISO. Un seul caractère les sépare.
 */
const toIso = (value: string): string => value.replace(" ", "T")

export function ExtraHoursRecapStep(props: ExtraHoursRecapStepProps) {
    const {
        draft,
        childFirstname,
        formatOptions,
        deadlineOptions,
        subjects,
        days,
        totalLabel,
        billing,
        autonomyNote,
        recapKind = "purchase",
        testID,
    } = props

    const isIndividual = draft.format === "individual"
    const formatLabel = formatOptions.find((o) => o.value === draft.format)?.label ?? ""
    const subjectLabel = subjects.find((s) => String(s.id) === String(draft.subjectId))?.name ?? ""

    const deadlineOption = deadlineOptions.find((o) => o.value === draft.deadlineType)
    const deadlineLabel =
        deadlineOption?.custom === true && draft.customDeadline != null
            ? `Avant le ${formatDayMonthYear(draft.customDeadline)}`
            : (deadlineOption?.label ?? "")

    // Les dates s'écrivent dans l'ordre où le parent les a données ; les créneaux se regroupent
    // par journée, comme le legacy — « Mercredi · 14h-15h, 15h-16h » plutôt que deux chips.
    const chosenLabels = isIndividual
        ? draft.requestedDates
              .filter((value): value is string => value != null && value !== "")
              .map((value) => formatDayTime(toIso(value)))
        : days
              .map((day) => {
                  const picked = day.slots.filter((slot) => draft.slotIds.includes(slot.id))
                  return picked.length === 0 ? null : `${day.label} · ${picked.map((slot) => slot.label).join(", ")}`
              })
              .filter((label): label is string => label != null)

    const billingText = billingMessage(billing, totalLabel)

    return (
        <Stack gap="5" testID={testID}>
            {/* La phrase qui manquait : c'est un achat, et il s'AJOUTE. */}
            <Paragraph>
                Vous {recapKind === "request" ? "demandez" : "achetez"} {draft.hours}h de cours pour {childFirstname},
                en plus de son abonnement : ses séances habituelles ne changent pas.
            </Paragraph>

            <Card p="4" background="surface-subtle">
                <Stack gap="3">
                    <SummaryRow label="Heures" value={`${draft.hours}h`} />
                    <SummaryRow label="Format" value={formatLabel} />
                    {!isIndividual && <SummaryRow label="À traiter" value={deadlineLabel} />}
                    <SummaryRow label="Matière" value={subjectLabel} />
                    <Divider />
                    <SummaryRow label="Prix TTC" value={totalLabel} emphasis />
                    {/* L'offre Autonomie explique le prix juste sous lui : ce qu'on ne facture pas,
                        ou pourquoi on facture. */}
                    {autonomyNote != null && (
                        <Text size="text-sm" color="text-strong">
                            {autonomyNote}
                        </Text>
                    )}
                </Stack>
            </Card>

            {/* Quand l'argent part se lit sous le prix, pas au fond du corps qui défile. L'équipe
                facture à la main une fois le cours planifié : sur le prochain prélèvement, ou tout
                de suite quand l'abonnement n'en a plus. */}
            {billingText != null && (
                <AlertCard severity="warning" title="Rien n'est prélevé aujourd'hui" message={billingText} />
            )}

            <Stack gap="2">
                <SectionLabel>{isIndividual ? "Dates demandées" : "Créneaux préférés"}</SectionLabel>
                <SlotChips
                    labels={chosenLabels}
                    emptyMessage={isIndividual ? "Aucune date sélectionnée" : "Aucun créneau sélectionné"}
                />
            </Stack>

            {/* Qui prendra le cours n'est pas une alerte, c'est une explication : elle se dit en
                encre secondaire, pas dans une boîte teintée de plus. */}
            <Text size="text-sm" color="text-muted">
                Nous essayons en priorité de planifier ce cours avec le tuteur habituel de votre enfant. En cas
                d&apos;indisponibilité, un autre tuteur compatible pourra le prendre en charge.
            </Text>
        </Stack>
    )
}
